import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertBell } from "@/components/AlertBell";
import { LayoutDashboard, Users, Send, Inbox, CheckSquare, Settings, LogOut, Compass, BarChart3, LifeBuoy, MailCheck, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["/", "Dashboard", LayoutDashboard], ["/leads", "Leads", Users], ["/review", "Review", ClipboardCheck], ["/campaigns", "Campaigns", Send], ["/outbox", "Outbox", MailCheck],
  ["/analytics", "Analytics", BarChart3],
  ["/inbox", "Inbox", Inbox], ["/tasks", "Tasks", CheckSquare], ["/help", "Help", LifeBuoy],
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [queued, setQueued] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    const f = () => api.get("/outbox/count").then((r) => setQueued(r.data.queued)).catch(() => {});
    f(); const t = setInterval(f, 60000); window.addEventListener("outbox-changed", f);
    return () => { clearInterval(t); window.removeEventListener("outbox-changed", f); };
  }, [loc.pathname]);
  const items = user?.role === "admin" ? [...NAV, ["/settings", "Settings", Settings]] : NAV;
  return (
    <div className="min-h-screen flex">
      <aside className="w-[72px] lg:w-60 shrink-0 bg-[#0F172A] text-slate-300 flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-4 lg:px-5 py-6 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-blue-600 grid place-items-center text-white"><Compass size={18} /></div>
          <div className="hidden lg:block leading-tight">
            <div className="text-white font-extrabold tracking-tight">Sales Navigator</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Borrow A Geek</div>
          </div>
        </div>
        <nav className="flex-1 px-2 lg:px-3 space-y-0.5">
          {items.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-blue-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              <span className="relative"><Icon size={17} />{to === "/outbox" && queued > 0 && <span className="lg:hidden absolute -top-1.5 -right-2 h-2 w-2 rounded-full bg-orange-500" />}</span><span className="hidden lg:inline">{label}</span>
              {to === "/outbox" && queued > 0 && <span className="hidden lg:inline ml-auto bg-orange-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid="outbox-nav-count">{queued}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="px-2 lg:px-3 pb-2"><AlertBell /></div>
        <div className="p-3 border-t border-slate-800">
          <div className="hidden lg:block px-2 pb-2">
            <div className="text-sm text-white truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.email} · {user?.role}</div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white">
            <LogOut size={16} /><span className="hidden lg:inline">Log out</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

export function PageHeader({ eyebrow, title, children }) {
  return (
    <div className="px-6 lg:px-10 pt-8 pb-6 flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 bg-white">
      <div className="fade-up">
        {eyebrow && <div className="label-xs mb-2">{eyebrow}</div>}
        <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export function StageBadge({ stage, testid }) {
  const labels = { not_reviewed: "0 · Not Reviewed", cold_email: "1 · Cold Email", followup_1: "2 · Focused Follow-up", followup_2: "3 · Focused Follow Up 2", close_lead: "4 · Close Lead" };
  return <span data-testid={testid} className={`stage-${stage} inline-flex items-center border rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap`}>{labels[stage] || stage}</span>;
}

export function ScoreChip({ score = 0 }) {
  const c = score >= 15 ? "bg-orange-500 text-white" : score >= 5 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600";
  return <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded ${c}`} title="Lead score">{score}</span>;
}
